import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import {
  IconTool,
  IconArchive,
  IconCalendar,
  IconCheckbox,
  IconEdit,
  IconPhoto,
  IconVideo,
  IconSearch,
  IconRobot,
  IconBrain,
  IconWorld,
  IconChartBar,
  IconFingerprint,
  IconCpu,
  IconDeviceLaptop,
  IconBrandYoutube,
  IconSparkle2,
  IconBrandOpenai,
  IconBrandGoogle,
  IconBolt,
  IconFileText,
  IconFlag,
  IconFolder,
  IconLayoutDashboard,
  IconLink,
  IconListCheck,
  IconMessages,
  IconNotes,
  IconPalette,
  IconPaperclip,
  IconTable,
  IconUsers
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

import ImageLoading from '@/components/ui/image-loading'
import Ripple from '@/components/ui/ripple'
import {
  resolveChatLosiPose,
  type ChatLosiPose
} from '@/lib/losi-avatar/chat-status'
import { useServerEvents } from '@/hooks/useServerEvents'
import { getPlatformToolActionLabel } from '@/lib/platformTools/utils/platformToolLabels'
import { getToolDisplayLabel } from '@/lib/tool-labels'

import { AnimatePresence, motion } from 'framer-motion'
import ShinyText from '../magicui/shiny-text'
import { ManimAnimationSkeleton } from '@/components/skeletons/manim-animation-skeleton'
import { renderToolResultForEntry } from '@/components/messages/annotations/tool-results'
import DesktopToolAnnotation, {
  isDesktopToolAnnotationKey
} from '@/components/messages/annotations/desktop-tool'
import type { ToolActivityEntry } from '@/components/messages/annotations/tool-activity-log'
import type { Annotation2 } from '@/types/annotation'
import { Reasoning, ReasoningContent, ReasoningTrigger } from '@/components/ai-elements/reasoning'

// Fisher-Yates shuffle function
const shuffleArray = (array: string[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[array[i], array[j]] = [array[j], array[i]]
  }
  return array
}

interface ToolExecutionInfo {
  toolId: string
  toolName: string
  status: 'running' | 'complete' | 'error'
  progress?: number
  message?: string
  request?: unknown
  result?: unknown
  progressData?: Record<string, unknown>
  error?: string
  step?: string
}

interface ToolStartEventData {
  chatId?: string | null
  toolId?: string
  toolName?: string
  request?: unknown
  displayTitle?: string
}

interface ToolStartEvent {
  id?: string
  createdAt?: string
  timestamp?: number
  data?: ToolStartEventData
}

interface LoadingMessageProps {
  isGenerating: boolean
  currentSection: 'thinking' | 'reflection' | 'output' | 'verification' | null
  showToolsOnly?: boolean
  chatId?: string | null
  generationStartedAt?: number | null
  /** Publishes the live Losi pose (tool markers + ambient) to the message avatar. */
  onPoseChange?: (pose: ChatLosiPose | null) => void
}

// Map tool IDs to friendly names, icons, and processing steps
const TOOL_DISPLAY_INFO: Record<
  string,
  {
    name: string
    icon: React.ReactNode
    description: string
    steps: string[]
  }
> = {
  // Image generation
  imageGenerator: {
    name: 'Creating image',
    icon: <IconPhoto className="size-4 text-muted-foreground" />,
    description: 'Generating a high-quality image from your description',
    steps: [
      'Interpreting your request',
      'Setting up image parameters',
      'Generating image',
      'Enhancing details',
      'Applying final touches',
      'Saving image'
    ]
  },
  gptImageFull: {
    name: 'Creating image',
    icon: <IconPhoto className="size-4 text-muted-foreground" />,
    description: 'Using Losi Pixel to create an image from your description',
    steps: [
      'Improving prompt',
      'Creating something special',
      'Enhancing details',
      'Applying final touches',
      'Generating image composition',
      'Rendering final image',
      'Optimizing result'
    ]
  },
  flux1Pro: {
    name: 'Creating image with Flux',
    icon: <IconPhoto className="size-4 text-muted-foreground" />,
    description: 'Using Flux to generate an artistic image',
    steps: [
      'Analyzing prompt details',
      'Setting art parameters',
      'Generating image composition',
      'Adding artistic elements',
      'Applying styling',
      'Finalizing image'
    ]
  },
  stableDiffusion: {
    name: 'Creating image',
    icon: <IconPhoto className="size-4 text-muted-foreground" />,
    description: 'Generating a customized image with Stable Diffusion',
    steps: [
      'Processing prompt',
      'Setting diffusion parameters',
      'Starting generation',
      'Denoising image',
      'Adding details',
      'Upscaling result',
      'Finalizing image'
    ]
  },

  // Video generation
  videoGenerator: {
    name: 'Creating video',
    icon: <IconVideo className="size-4 text-muted-foreground" />,
    description: 'Generating a short video from your description',
    steps: [
      'Interpreting your request',
      'Setting video parameters',
      'Generating frames',
      'Building animation',
      'Adding motion elements',
      'Rendering final video',
      'Optimizing output'
    ]
  },
  studioVideoGenerator: {
    name: 'Creating studio-quality video',
    icon: <IconVideo className="size-4 text-muted-foreground" />,
    description: 'Creating a high-quality video with advanced effects',
    steps: [
      'Analyzing your request',
      'Setting professional parameters',
      'Creating initial composition',
      'Generating frame sequence',
      'Adding advanced motion',
      'Applying studio effects',
      'Rendering high-quality video',
      'Optimizing final output'
    ]
  },

  // Web tools
  webScraper__youtubeCaptions: {
    name: 'Retrieving information',
    icon: <IconWorld className="size-4 text-muted-foreground" />,
    description: 'Fetching and processing information from the web',
    steps: [
      'Analyzing your query',
      'Searching the internet',
      'Finding relevant sources',
      'Extracting key information',
      'Synthesizing content',
      'Formatting results'
    ]
  },
  webScraper__FetchDataFromUrl: {
    name: 'Retrieving information',
    icon: <IconWorld className="size-4 text-muted-foreground" />,
    description: 'Fetching and processing information from the web',
    steps: [
      'Analyzing your query',
      'Searching the internet',
      'Finding relevant sources',
      'Extracting key information',
      'Synthesizing content',
      'Formatting results'
    ]
  },
  webScraper__googleSearch: {
    name: 'Web search',
    icon: <IconSearch className="size-4 text-muted-foreground" />,
    description: 'Finding information on the web to answer your question',
    steps: [
      'Formulating search query',
      'Searching the internet',
      'Reviewing top results',
      'Analyzing relevant pages',
      'Extracting key information',
      'Organizing search findings'
    ]
  },
  webScraper__getWeather: {
    name: 'Getting weather information',
    icon: <IconWorld className="size-4 text-muted-foreground" />,
    description: 'Retrieving current weather data for your location',
    steps: [
      'Determining location coordinates',
      'Fetching weather data',
      'Processing forecast information',
      'Retrieving temperature details',
      'Preparing weather summary'
    ]
  },
  yahooFinance: {
    name: 'Getting financial data',
    icon: <IconChartBar className="size-4 text-muted-foreground" />,
    description: 'Retrieving up-to-date financial information',
    steps: [
      'Identifying financial symbols',
      'Connecting to data sources',
      'Fetching latest market data',
      'Processing financial information',
      'Analyzing stock performance',
      'Preparing financial summary'
    ]
  },

  // Task automation
  taskAutomation: {
    name: 'Setting up automation',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Creating an automated task based on your requirements',
    steps: [
      'Analyzing your automation request',
      'Designing workflow steps',
      'Setting up trigger conditions',
      'Configuring action parameters',
      'Establishing scheduling rules',
      'Creating automation task'
    ]
  },

  // Memory and vector tools
  memoryTool: {
    name: 'Storing memory',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Saving important information for future reference',
    steps: [
      'Processing memory content',
      'Organizing information',
      'Storing in memory database',
      'Creating memory connections',
      'Confirming memory storage'
    ]
  },
  vectorScratchpadTool: {
    name: 'Processing with vector memory',
    icon: <IconFingerprint className="size-4 text-muted-foreground" />,
    description: 'Using semantic memory to find relevant information',
    steps: [
      'Converting request to vector',
      'Searching vector database',
      'Finding semantic matches',
      'Calculating relevance scores',
      'Retrieving similar memories',
      'Processing vector results'
    ]
  },
  deepResearchPlatformTool: {
    name: 'Researching',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Researching the web for information',
    steps: [
      'Analyzing your request',
      'Searching online sources',
      'Gathering relevant information',
      'Synthesizing findings',
      'Preparing research summary'
    ]
  },
  VirtualFilesystem: {
    name: 'Virtual filesystem',
    icon: <IconFileText className="size-4 text-muted-foreground" />,
    description: 'Creating or updating files in your workspace',
    steps: [
      'Writing file content',
      'Saving to workspace',
      'Updating virtual files',
      'Confirming changes'
    ]
  },
  show_plan: {
    name: 'Updating plan',
    icon: <IconFileText className="size-4 text-muted-foreground" />,
    description: 'Creating or updating the conversation plan',
    steps: ['Saving plan content', 'Updating canvas', 'Syncing plan']
  },
  set_todos: {
    name: 'Updating steps',
    icon: <IconListCheck className="size-4 text-muted-foreground" />,
    description: "Setting Losi's steps for this conversation",
    steps: ['Saving step list', 'Updating task tracker', 'Syncing steps']
  },
  update_todo_status: {
    name: 'Updating step status',
    icon: <IconListCheck className="size-4 text-muted-foreground" />,
    description: "Updating a step's status",
    steps: ['Updating status', 'Syncing task tracker']
  },
  edit_canvas: {
    name: 'Editing canvas',
    icon: <IconFileText className="size-4 text-muted-foreground" />,
    description: 'Applying edits to the canvas content',
    steps: ['Applying patch', 'Updating content', 'Syncing canvas']
  },
  rename_chat: {
    name: 'Renaming chat',
    icon: <IconEdit className="size-4 text-muted-foreground" />,
    description: 'Updating the conversation title',
    steps: ['Checking current title', 'Saving new title', 'Syncing chat list']
  },
  // Add Manim animation tool info
  manimAnimation: {
    name: 'Creating mathematical animation',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Generating a mathematical animation with Manim',
    steps: [
      'Parsing request',
      'Setting up animation parameters',
      'Processing scene',
      'Rendering animation',
      'Compiling video',
      'Preparing final result'
    ]
  },
  // Add Manim animation tool info
  renderManimAnimation: {
    name: 'Creating mathematical animation',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Generating a mathematical animation with Manim',
    steps: [
      'Parsing request',
      'Setting up animation parameters',
      'Processing scene',
      'Rendering animation',
      'Compiling video',
      'Preparing final result'
    ]
  },
  webScraper__renderManimAnimation: {
    name: 'Creating mathematical animation',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Generating a mathematical animation with Manim',
    steps: [
      'Parsing request',
      'Setting up animation parameters',
      'Processing scene',
      'Rendering animation',
      'Compiling video',
      'Preparing final result'
    ]
  },
  webScraper__manimAnimation: {
    name: 'Creating mathematical animation',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'Generating a mathematical animation with Manim',
    steps: [
      'Parsing request',
      'Setting up animation parameters',
      'Processing scene',
      'Rendering animation',
      'Compiling video',
      'Preparing final result'
    ]
  },

  // Connected services
  ConnectedServices: {
    name: 'Accessing connected accounts',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Interacting with your connected accounts and services',
    steps: [
      'Connecting to your account',
      'Retrieving account information',
      'Processing your request',
      'Executing action',
      'Preparing results'
    ]
  },
  ConnectedServices__listConnectedExtensions: {
    name: 'Loading connected accounts',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Retrieving your connected accounts and services',
    steps: [
      'Connecting to your account',
      'Fetching connected services',
      'Verifying account status',
      'Preparing account list'
    ]
  },
  ConnectedServices__listActionsForExtension: {
    name: 'Loading available actions',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Retrieving available actions for your connected account',
    steps: [
      'Connecting to your account',
      'Fetching available actions',
      'Analyzing action capabilities',
      'Preparing action list'
    ]
  },
  ConnectedServices__executeExtensionAction: {
    name: 'Executing action',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Executing an action on your connected account',
    steps: [
      'Connecting to your account',
      'Preparing action parameters',
      'Executing action',
      'Processing results',
      'Preparing response'
    ]
  },

  // CRM Tools
  create_lead: {
    name: 'Creating lead',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Adding a new lead to your CRM',
    steps: [
      'Validating lead information',
      'Checking for duplicates',
      'Creating lead record',
      'Setting up initial status',
      'Preparing lead details'
    ]
  },
  update_lead: {
    name: 'Updating lead',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Updating lead information in your CRM',
    steps: [
      'Locating lead record',
      'Validating updates',
      'Applying changes',
      'Updating CRM database',
      'Confirming updates'
    ]
  },
  bulk_update_leads: {
    name: 'Updating leads',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Updating multiple leads in your CRM',
    steps: [
      'Validating selection',
      'Applying updates',
      'Updating CRM database',
      'Confirming changes'
    ]
  },
  search_leads: {
    name: 'Searching leads',
    icon: <IconSearch className="size-4 text-muted-foreground" />,
    description: 'Finding leads in your CRM',
    steps: [
      'Analyzing search query',
      'Searching CRM database',
      'Filtering results',
      'Ranking matches',
      'Preparing lead list'
    ]
  },
  get_lead_details: {
    name: 'Loading lead details',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Retrieving lead information and activity logs',
    steps: [
      'Locating lead record',
      'Loading lead details',
      'Fetching activity history',
      'Gathering status information',
      'Preparing comprehensive view'
    ]
  },
  get_email_engagement: {
    name: 'Analyzing email engagement',
    icon: <IconSearch className="size-4 text-muted-foreground" />,
    description: 'Analyzing email opens, clicks, and replies',
    steps: [
      'Loading email history',
      'Analyzing open rates',
      'Calculating click-through rates',
      'Measuring reply rates',
      'Computing engagement score'
    ]
  },
  get_lead_insights: {
    name: 'Generating lead insights',
    icon: <IconBrain className="size-4 text-muted-foreground" />,
    description: 'AI-powered analysis of lead health and recommendations',
    steps: [
      'Analyzing lead data',
      'Calculating health score',
      'Reviewing engagement patterns',
      'Generating insights',
      'Creating recommendations'
    ]
  },
  suggest_follow_up: {
    name: 'Suggesting follow-up',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'AI-powered follow-up timing and content suggestions',
    steps: [
      'Analyzing engagement history',
      'Reviewing activity patterns',
      'Determining optimal timing',
      'Generating content suggestions',
      'Preparing recommendations'
    ]
  },
  send_email: {
    name: 'Sending email',
    icon: <IconWorld className="size-4 text-muted-foreground" />,
    description: 'Sending an email through your CRM',
    steps: [
      'Preparing email content',
      'Validating recipient',
      'Sending email',
      'Tracking delivery',
      'Logging activity'
    ]
  },
  send_sms: {
    name: 'Sending SMS',
    icon: <IconWorld className="size-4 text-muted-foreground" />,
    description: 'Sending a text message through your CRM',
    steps: [
      'Preparing message content',
      'Validating phone number',
      'Sending SMS',
      'Tracking delivery',
      'Logging activity'
    ]
  },
  schedule_activity: {
    name: 'Scheduling activity',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Scheduling a follow-up activity',
    steps: [
      'Setting activity details',
      'Checking availability',
      'Creating schedule entry',
      'Setting reminders',
      'Confirming schedule'
    ]
  },
  log_activity: {
    name: 'Logging activity',
    icon: <IconTool className="size-4 text-muted-foreground" />,
    description: 'Recording a completed activity',
    steps: [
      'Capturing activity details',
      'Recording outcome',
      'Updating CRM records',
      'Logging timestamp',
      'Confirming log entry'
    ]
  },

  // Agent Management Tools
  list_agents: {
    name: 'Listing agents',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Retrieving your Nexus agents',
    steps: [
      'Loading workspace agents',
      'Filtering results',
      'Gathering agent details',
      'Preparing agent list'
    ]
  },
  get_agent: {
    name: 'Loading agent',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Retrieving agent details',
    steps: [
      'Locating agent record',
      'Loading configuration',
      'Gathering capabilities',
      'Preparing agent details'
    ]
  },
  create_agent: {
    name: 'Creating agent',
    icon: <IconSparkle2 className="size-4 text-muted-foreground" />,
    description: 'Setting up a new Nexus agent',
    steps: [
      'Validating agent configuration',
      'Setting up models',
      'Configuring capabilities',
      'Creating agent record',
      'Initializing agent'
    ]
  },
  update_agent: {
    name: 'Updating agent',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Updating agent configuration',
    steps: [
      'Locating agent record',
      'Validating updates',
      'Applying changes',
      'Updating configuration',
      'Confirming updates'
    ]
  },
  improve_agent: {
    name: 'Improving agent',
    icon: <IconSparkle2 className="size-4 text-muted-foreground" />,
    description: 'Applying improvements to your agent',
    steps: [
      'Analyzing current configuration',
      'Applying improvements',
      'Enabling capabilities',
      'Updating settings',
      'Confirming improvements'
    ]
  },

  // Sidecar (system tool – delegate to workers)
  Sidecar__delegate: {
    name: 'Sidecar',
    icon: <IconRobot className="size-4 text-muted-foreground" />,
    description: 'Delegating to Sidecar agents',
    steps: [
      'Analyzing objective',
      'Selecting agents',
      'Running workers',
      'Gathering results',
      'Synthesizing answer'
    ]
  }
}

// Default tool info for unknown tools
const DEFAULT_TOOL_INFO = {
  name: 'Using tool',
  icon: <IconTool className="size-4 text-muted-foreground" />,
  description: 'Processing your request with a specialized tool',
  steps: ['Analyzing your request', 'Processing information', 'Generating results']
}

const LOSI_SPACES_RUNNING_TOOL_LABELS: Record<string, string> = {
  listSpaces: 'Listing Spaces',
  createSpace: 'Creating Space',
  updateSpace: 'Updating Space',
  archiveSpace: 'Archiving Space',
  listSpaceTasks: 'Listing tasks',
  createSpaceTask: 'Creating task',
  updateSpaceTask: 'Updating task',
  deleteSpaceTask: 'Deleting task',
  listTaskSubtasks: 'Listing subtasks',
  createTaskSubtask: 'Creating subtask',
  updateTaskSubtask: 'Updating subtask',
  deleteTaskSubtask: 'Deleting subtask',
  listTaskChecklistItems: 'Listing checklist',
  createTaskChecklistItem: 'Creating checklist item',
  updateTaskChecklistItem: 'Updating checklist item',
  deleteTaskChecklistItem: 'Deleting checklist item',
  listTaskNotes: 'Listing task notes',
  deleteTaskNote: 'Deleting task note',
  listTaskLinkedItems: 'Listing linked items',
  searchTaskAttachableItems: 'Searching attachable items',
  attachTaskLinkedItem: 'Attaching item',
  removeTaskLinkedItem: 'Removing item',
  listSpaceNotes: 'Listing notes',
  getSpaceNote: 'Reading note',
  createSpaceNote: 'Creating note',
  updateSpaceNote: 'Updating note',
  deleteSpaceNote: 'Deleting note',
  listSpaceFolders: 'Listing folders',
  createSpaceFolder: 'Creating folder',
  updateSpaceFolder: 'Updating folder',
  listSpaceSheets: 'Listing sheets',
  getSpaceSheet: 'Reading sheet',
  createSpaceSheet: 'Creating sheet',
  updateSpaceSheet: 'Updating sheet',
  deleteSpaceSheet: 'Deleting sheet',
  replaceSpaceSheetWorkbook: 'Replacing sheet workbook',
  applySpaceSheetRangeUpdate: 'Updating sheet range',
  applySpaceSheetRangeFill: 'Filling sheet range',
  applySpaceSheetRangeTransfer: 'Transferring sheet range',
  applySpaceSheetStructureUpdate: 'Updating sheet structure',
  applySpaceSheetDimensionUpdate: 'Updating sheet dimensions',
  applySpaceSheetVisibilityUpdate: 'Updating sheet visibility',
  listSpaceSprints: 'Listing sprints',
  createSpaceSprint: 'Creating sprint',
  updateSpaceSprint: 'Updating sprint',
  listSpaceEvents: 'Listing events',
  createSpaceEvent: 'Creating event',
  updateSpaceEvent: 'Updating event',
  listManagedCalendars: 'Listing managed calendars',
  listManagedCalendarEvents: 'Listing managed calendar events',
  createManagedCalendarEvent: 'Creating managed event',
  updateManagedCalendarEvent: 'Updating managed event',
  deleteManagedCalendarEvent: 'Deleting managed event',
  checkManagedCalendarAvailability: 'Checking calendar availability',
  listSpaceFiles: 'Listing files',
  searchSpaceFiles: 'Searching files',
  attachSpaceFile: 'Attaching file',
  updateSpaceFile: 'Updating file',
  detachSpaceFile: 'Detaching file',
  listSpaceCollections: 'Listing collections',
  attachSpaceCollection: 'Attaching collection',
  updateSpaceCollection: 'Updating collection',
  detachSpaceCollection: 'Detaching collection',
  listSpaceMembers: 'Listing members',
  listSpaceMemberDirectory: 'Finding workspace members',
  addSpaceMember: 'Adding Space member',
  inviteToSpace: 'Inviting to Space',
  updateSpaceMemberRole: 'Updating member role',
  removeSpaceMember: 'Removing member',
  listSpaceChats: 'Listing chats',
  linkSpaceChat: 'Linking chat',
  moveSpaceChat: 'Moving chat',
  unlinkSpaceChat: 'Unlinking chat',
  listSpaceConversations: 'Listing conversations',
  startSpaceConversation: 'Creating conversation',
  getSpaceConversation: 'Opening conversation',
  updateSpaceConversation: 'Updating conversation',
  archiveSpaceConversation: 'Archiving conversation',
  duplicateSpaceConversation: 'Duplicating conversation',
  listSpaceConversationMessages: 'Listing conversation messages',
  respondSpaceConversation: 'Replying in conversation',
  listSpaceWhiteboards: 'Listing whiteboards',
  createSpaceWhiteboard: 'Creating whiteboard',
  updateSpaceWhiteboard: 'Updating whiteboard',
  deleteSpaceWhiteboard: 'Deleting whiteboard',
  listSpaceDesigns: 'Listing designs',
  getSpaceDesign: 'Opening design',
  createSpaceDesign: 'Creating design',
  updateSpaceDesign: 'Updating design',
  deleteSpaceDesign: 'Deleting design',
  applySpaceDesignSceneMutation: 'Updating design scene'
}

function getLosiSpacesToolIcon(functionId: string): React.ReactNode {
  const normalized = functionId.toLowerCase()

  if (normalized.includes('spacefile')) {
    return <IconPaperclip className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacecollection')) {
    return <IconArchive className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacesheet')) {
    return <IconTable className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacesprint')) {
    return <IconFlag className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spaceevent') || normalized.includes('calendar')) {
    return <IconCalendar className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacedesign') || normalized.includes('whiteboard')) {
    return <IconPalette className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacenote')) {
    return <IconNotes className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacefolder')) {
    return <IconFolder className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('spacechat') || normalized.includes('conversation')) {
    return <IconMessages className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('member') || normalized.includes('invite')) {
    return <IconUsers className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('linkeditem') || normalized.includes('attachable')) {
    return <IconLink className="size-4 text-muted-foreground" />
  }
  if (
    normalized.includes('task') ||
    normalized.includes('checklist') ||
    normalized.includes('subtask')
  ) {
    return <IconCheckbox className="size-4 text-muted-foreground" />
  }
  if (normalized.includes('space')) {
    return <IconLayoutDashboard className="size-4 text-muted-foreground" />
  }

  return <IconTool className="size-4 text-muted-foreground" />
}

type RunningComputerResult = Record<string, unknown>

const COMPUTER_TOOL_OPERATIONS: Record<string, string> = {
  sandbox__runCode: 'code',
  sandbox__getWorkspace: 'workspace',
  sandbox__runCommand: 'command',
  sandbox__publishFiles: 'files',
  sandbox__writeFiles: 'files',
  sandbox__startPreview: 'preview',
  sandbox__runAgentBrowser: 'browser',
  codeInterpreter: 'code',
  codeExecutionTool: 'code'
}

type RunningBrowserNetworkStatus =
  | 'verified'
  | 'failed'
  | 'pending'
  | 'not checked'

const RUNNING_BROWSER_PROXY_KEYS = [
  'proxy',
  'proxyUrl',
  'agentBrowserProxy',
  'httpProxy',
  'httpsProxy',
  'socksProxy'
] as const

const RUNNING_BROWSER_PROXY_HOST_KEYS = [
  'host',
  'hostname',
  'server',
  'endpoint',
  'address'
] as const

const RUNNING_BROWSER_PROXY_BYPASS_KEYS = [
  'bypass',
  'bypassList',
  'proxyBypass',
  'proxyBypassList',
  'noProxy',
  'no_proxy',
  'excludedHosts'
] as const

// --------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

// --------------

function getRequestParams(request: unknown) {
  const record = asRecord(request)
  if (!record) return {}

  const parameters = asRecord(record.parameters)
  const input = asRecord(record.input)
  const args = asRecord(record.args)

  return {
    ...record,
    ...(parameters ?? {}),
    ...(input ?? {}),
    ...(args ?? {})
  }
}

// --------------

function readRecordString(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : null
}

// --------------

function readRecordNumber(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

// --------------

function readRecordBoolean(record: Record<string, unknown>, key: string) {
  const value = record[key]
  return typeof value === 'boolean' ? value : null
}

// --------------

function readRecordStringArray(record: Record<string, unknown>, key: string) {
  const value = record[key]
  if (!Array.isArray(value)) return []

  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

// --------------

function getComputerOperation(displayKey: string) {
  if (displayKey in COMPUTER_TOOL_OPERATIONS) {
    return COMPUTER_TOOL_OPERATIONS[displayKey]
  }

  if (displayKey.startsWith('sandbox__')) {
    return 'workspace'
  }

  return null
}

// --------------

function isComputerLoadingTool(displayKey: string) {
  return Boolean(getComputerOperation(displayKey))
}

// --------------

function buildCommandLineFromParams(params: Record<string, unknown>) {
  const cmd = readRecordString(params, 'cmd') ?? readRecordString(params, 'command')
  if (!cmd) return null

  const args = readRecordStringArray(params, 'args')
  return [cmd, ...args].join(' ')
}

// --------------

function buildRunningSandboxSummary(
  displayKey: string,
  params: Record<string, unknown>,
  operation: string
) {
  const template =
    readRecordString(params, 'template') ??
    (displayKey === 'sandbox__runAgentBrowser' ? 'agent-browser' : null)
  const runtime =
    readRecordString(params, 'runtime') ??
    (readRecordString(params, 'language') === 'python' ? 'python3.13' : 'node24')
  const name =
    readRecordString(params, 'sandboxName') ??
    readRecordString(params, 'name') ??
    'Active computer'

  return {
    name,
    persistent: operation !== 'code',
    runtime,
    status: 'running',
    ...(template
      ? {
          template: {
            name: template,
            snapshotConfigured: false
          }
        }
      : {})
  }
}

// --------------

function getProgressPayloadRecord(progressData: Record<string, unknown> | undefined) {
  return asRecord(progressData?.progress)
}

// --------------

function getRunningBrowserProgressRecord(
  progressData: Record<string, unknown> | undefined
) {
  const progressRecord = getProgressPayloadRecord(progressData)
  return asRecord(progressData?.browser) ?? asRecord(progressRecord?.browser)
}

// --------------

function getRunningBrowserNetworkRecord(
  params: Record<string, unknown>,
  progressData: Record<string, unknown> | undefined,
  progressBrowser: Record<string, unknown> | null
) {
  const progressRecord = getProgressPayloadRecord(progressData)
  const paramsProxy = asRecord(params.proxy)
  const paramsNetwork = asRecord(params.network)
  const progressNetwork =
    asRecord(progressBrowser?.network) ??
    asRecord(progressData?.network) ??
    asRecord(progressRecord?.network)
  const progressProxy =
    asRecord(progressBrowser?.proxy) ??
    asRecord(progressNetwork?.proxy) ??
    asRecord(progressData?.proxy) ??
    asRecord(progressRecord?.proxy)
  const progressRoute = asRecord(progressNetwork?.route)

  return [
    params,
    paramsProxy,
    paramsNetwork,
    progressBrowser,
    progressProxy,
    progressNetwork,
    progressRoute,
    progressData,
    progressRecord
  ].filter((record): record is Record<string, unknown> => Boolean(record))
}

// --------------

function getFirstRunningBrowserString(
  records: Record<string, unknown>[],
  keys: readonly string[]
) {
  for (const record of records) {
    for (const key of keys) {
      const value = readRecordString(record, key)
      if (value) return value
    }
  }

  return null
}

// --------------

function sanitizeRunningBrowserProxyHost(value: string | null) {
  const trimmed = value?.trim()
  if (!trimmed) return null

  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `http://${trimmed}`)
    return parsed.host || null
  } catch {
    const authority = trimmed
      .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
      .split(/[/?#]/)[0]
      ?.split('@')
      .pop()
      ?.trim()

    return authority && !/\s/.test(authority) ? authority : null
  }
}

// --------------

function readRunningBrowserStringList(record: Record<string, unknown>, key: string) {
  const value = record[key]
  const items = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(/[,\n]/)
      : []

  return items
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean)
}

// --------------

function getRunningBrowserProxyBypassList(records: Record<string, unknown>[]) {
  const seen = new Set<string>()
  const bypassList: string[] = []

  for (const record of records) {
    for (const key of RUNNING_BROWSER_PROXY_BYPASS_KEYS) {
      for (const item of readRunningBrowserStringList(record, key)) {
        const sanitized = sanitizeRunningBrowserProxyHost(item) ?? item
        if (seen.has(sanitized)) continue

        seen.add(sanitized)
        bypassList.push(sanitized)
      }
    }
  }

  return bypassList.slice(0, 12)
}

// --------------

function normalizeRunningBrowserNetworkStatus(
  value: string | null
): RunningBrowserNetworkStatus | null {
  const normalized = value?.trim().toLowerCase()
  if (!normalized) return null

  if (['verified', 'success', 'ok', 'passed', 'reachable'].includes(normalized)) {
    return 'verified'
  }

  if (['failed', 'failure', 'error', 'blocked', 'unreachable'].includes(normalized)) {
    return 'failed'
  }

  if (['pending', 'running', 'checking', 'started'].includes(normalized)) {
    return 'pending'
  }

  return null
}

// --------------

function getRunningBrowserNetworkStatus(records: Record<string, unknown>[]) {
  for (const record of records) {
    const verified = readRecordBoolean(record, 'verified')
    if (verified === true) return 'verified'
    if (verified === false) return 'failed'

    const verification = asRecord(record.verification)
    const status =
      normalizeRunningBrowserNetworkStatus(
        verification ? readRecordString(verification, 'status') : null
      ) ??
      normalizeRunningBrowserNetworkStatus(readRecordString(record, 'verificationStatus')) ??
      normalizeRunningBrowserNetworkStatus(readRecordString(record, 'status'))

    if (status) return status
  }

  return null
}

// --------------

function buildRunningBrowserProxyRecord(
  params: Record<string, unknown>,
  progressData: Record<string, unknown> | undefined,
  progressBrowser: Record<string, unknown> | null
) {
  const records = getRunningBrowserNetworkRecord(params, progressData, progressBrowser)
  const proxyUrl = getFirstRunningBrowserString(records, RUNNING_BROWSER_PROXY_KEYS)
  const explicitHost = getFirstRunningBrowserString(records, RUNNING_BROWSER_PROXY_HOST_KEYS)
  const host =
    sanitizeRunningBrowserProxyHost(explicitHost) ??
    sanitizeRunningBrowserProxyHost(proxyUrl)
  const mode = getFirstRunningBrowserString(records, ['mode'])
  const hasCustomProxy =
    Boolean(proxyUrl || host) ||
    mode === 'custom' ||
    mode === 'proxy' ||
    mode === 'manual'

  if (!hasCustomProxy) return null

  const bypassList = getRunningBrowserProxyBypassList(records)
  const verificationStatus = getRunningBrowserNetworkStatus(records) ?? 'pending'

  return {
    mode: 'custom',
    ...(host ? { host } : {}),
    ...(bypassList.length > 0 ? { bypass: bypassList } : {}),
    verificationStatus,
    status: verificationStatus,
    usernameProvided: Boolean(getFirstRunningBrowserString(records, ['proxyUsername'])),
    passwordProvided: Boolean(getFirstRunningBrowserString(records, ['proxyPassword']))
  }
}

// --------------

function buildRunningBrowserNetwork(
  params: Record<string, unknown>,
  progressData: Record<string, unknown> | undefined,
  progressBrowser: Record<string, unknown> | null,
  proxy: Record<string, unknown> | null
) {
  const records = getRunningBrowserNetworkRecord(params, progressData, progressBrowser)
  const mode = getFirstRunningBrowserString(records, ['mode'])
  const verificationStatus = getRunningBrowserNetworkStatus(records)

  if (!proxy && !mode && !verificationStatus) return null

  const resolvedStatus = verificationStatus ?? (proxy ? 'pending' : 'not checked')

  return {
    mode: proxy ? 'custom' : mode ?? 'direct',
    verificationStatus: resolvedStatus,
    status: resolvedStatus,
    ...(proxy ? { proxy } : {})
  }
}

// --------------

function buildRunningBrowserHandoff(
  params: Record<string, unknown>,
  progressData: Record<string, unknown> | undefined,
  progressBrowser: Record<string, unknown> | null,
  provider: string
) {
  const progressHandoff =
    asRecord(progressBrowser?.handoff) ?? asRecord(progressData?.handoff)
  const required =
    readRecordBoolean(params, 'handoffRequired') ??
    (progressBrowser ? readRecordBoolean(progressBrowser, 'handoffRequired') : null) ??
    (progressHandoff ? readRecordBoolean(progressHandoff, 'required') : null) ??
    (provider === 'ios' ? true : null)
  const kind =
    readRecordString(params, 'handoffKind') ??
    (progressBrowser ? readRecordString(progressBrowser, 'handoffKind') : null) ??
    (progressHandoff ? readRecordString(progressHandoff, 'kind') : null) ??
    (provider === 'ios' ? 'authentication' : null)
  const reason =
    readRecordString(params, 'handoffReason') ??
    (progressBrowser ? readRecordString(progressBrowser, 'handoffReason') : null) ??
    (progressHandoff ? readRecordString(progressHandoff, 'reason') : null) ??
    (provider === 'ios'
      ? 'iOS Simulator needs a Mac runner before automation can continue.'
      : null)

  if (!required && !kind && !reason) return null

  return {
    required: true,
    kind: kind ?? 'other',
    reason: reason ?? 'A protected step needs you before automation continues.',
    action: 'takeover'
  }
}

// --------------

function buildRunningBrowserRecord(
  params: Record<string, unknown>,
  progressData: Record<string, unknown> | undefined
) {
  const progressBrowser = getRunningBrowserProgressRecord(progressData)
  const progressStream =
    asRecord(progressBrowser?.stream) ??
    asRecord(progressData?.stream)
  const progressDashboard =
    asRecord(progressBrowser?.dashboard) ??
    asRecord(progressData?.dashboard)
  const provider =
    readRecordString(params, 'provider') ??
    (progressBrowser ? readRecordString(progressBrowser, 'provider') : null) ??
    'chromium'
  const action =
    readRecordString(params, 'action') ??
    (progressBrowser ? readRecordString(progressBrowser, 'action') : null) ??
    'snapshot'
  const url =
    readRecordString(params, 'url') ??
    (progressBrowser ? readRecordString(progressBrowser, 'url') : null)
  const device =
    readRecordString(params, 'device') ??
    readRecordString(params, 'iosDevice') ??
    (progressBrowser ? readRecordString(progressBrowser, 'device') : null)
  const udid =
    readRecordString(params, 'udid') ??
    readRecordString(params, 'iosUdid') ??
    (progressBrowser ? readRecordString(progressBrowser, 'udid') : null)
  const streamEnabled = progressStream ? readRecordBoolean(progressStream, 'enabled') : null
  const rawStreamUrl =
    (progressStream ? readRecordString(progressStream, 'webSocketUrl') : null) ??
    (progressStream ? readRecordString(progressStream, 'url') : null) ??
    (progressBrowser ? readRecordString(progressBrowser, 'streamWebSocketUrl') : null) ??
    (progressBrowser ? readRecordString(progressBrowser, 'streamUrl') : null)
  const streamUrl = streamEnabled === false ? null : rawStreamUrl
  const streamError =
    (progressStream ? readRecordString(progressStream, 'error') : null) ??
    (progressBrowser ? readRecordString(progressBrowser, 'streamError') : null)
  const dashboardUrl =
    (progressDashboard && readRecordBoolean(progressDashboard, 'enabled') !== false
      ? readRecordString(progressDashboard, 'url')
      : null) ??
    (progressStream ? readRecordString(progressStream, 'dashboardUrl') : null) ??
    (progressBrowser ? readRecordString(progressBrowser, 'dashboardUrl') : null)
  const artifactPath =
    readRecordString(params, 'artifactPath') ??
    readRecordString(params, 'outputPath') ??
    readRecordString(params, 'path')
  const selector = readRecordString(params, 'selector')
  const filePaths = readRecordStringArray(params, 'filePaths')
  const clipboardText =
    readRecordString(params, 'text') ?? readRecordString(params, 'clipboardText')
  const proxy = buildRunningBrowserProxyRecord(params, progressData, progressBrowser)
  const network = buildRunningBrowserNetwork(params, progressData, progressBrowser, proxy)
  const handoff = buildRunningBrowserHandoff(
    params,
    progressData,
    progressBrowser,
    provider
  )

  const browser: Record<string, unknown> = {
    provider,
    action,
    ...(url ? { url } : {}),
    ...(device ? { device } : {}),
    ...(udid ? { udid } : {}),
    ...(provider === 'ios' ? { hostRequirement: 'macos-xcode-appium' } : {}),
    ...(streamUrl
      ? {
          streamWebSocketUrl: streamUrl,
          streamUrl,
          stream: {
            enabled: true,
            url: streamUrl,
            webSocketUrl: streamUrl,
            ...(dashboardUrl ? { dashboardUrl } : {})
          }
        }
      : {}),
    ...(!streamUrl && streamError
      ? {
          stream: {
            enabled: false,
            error: streamError,
            ...(dashboardUrl ? { dashboardUrl } : {})
          }
        }
      : {}),
    ...(dashboardUrl ? { dashboardUrl } : {}),
    ...(proxy ? { proxy } : {}),
    ...(network ? { network } : {}),
    ...(handoff
      ? {
          handoffRequired: true,
          handoffKind: handoff.kind,
          handoffReason: handoff.reason,
          handoff
        }
      : {})
  }

  if (action === 'recordStart' || action === 'recordRestart' || action === 'recordStop') {
    browser.recording = {
      action,
      state: action === 'recordStop' ? 'stopping' : 'recording',
      ...(artifactPath ? { path: artifactPath } : {}),
      format: 'webm'
    }
  }

  if (action === 'profilerStart' || action === 'profilerStop') {
    browser.profile = {
      action,
      state: action === 'profilerStart' ? 'recording' : 'saving',
      ...(artifactPath ? { path: artifactPath } : {}),
      format: 'json'
    }
  }

  if (action === 'traceStart' || action === 'traceStop') {
    browser.trace = {
      action,
      state: action === 'traceStart' ? 'recording' : 'saving',
      ...(artifactPath ? { path: artifactPath } : {}),
      format: 'json'
    }
  }

  if (action === 'console' || action === 'errors') {
    browser.diagnostics = {
      kind: action,
      cleared: params.clear === true
    }
  }

  if (action === 'vitals') {
    browser.metrics = { kind: 'vitals' }
  }

  if (action === 'reactTree') {
    browser.react = { action }
  }

  if (action === 'clipboardRead' || action === 'clipboardWrite') {
    browser.clipboard = {
      action,
      ...(clipboardText ? { text: clipboardText } : {})
    }
  }

  if (action === 'pdf') {
    browser.artifact = {
      kind: 'pdf',
      ...(artifactPath ? { path: artifactPath } : {}),
      format: 'pdf'
    }
  }

  if (action === 'upload' || action === 'download' || action === 'waitDownload') {
    browser.transfer = {
      action,
      ...(selector ? { selector } : {}),
      ...(artifactPath ? { path: artifactPath } : {}),
      ...(filePaths.length > 0 ? { paths: filePaths } : {})
    }
  }

  return browser
}

// --------------

function buildRunningComputerResult(
  toolExecution: ToolExecutionInfo,
  displayKey: string
): RunningComputerResult {
  const params = getRequestParams(toolExecution.request)
  const progressData = toolExecution.progressData
  const operation = getComputerOperation(displayKey) ?? 'workspace'
  const language =
    readRecordString(params, 'language') ??
    readRecordString(params, 'runtime') ??
    'node'
  const commandLine = buildCommandLineFromParams(params)
  const outputMessage =
    toolExecution.message ??
    (operation === 'browser'
      ? 'Preparing live browser session...'
      : operation === 'preview'
        ? 'Starting preview server...'
        : 'Running in computer...')
  const progressSandbox = asRecord(progressData?.sandbox)
  const result: RunningComputerResult = {
    success: true,
    operation,
    language,
    runtime: language === 'python' ? 'python3.13' : language,
    message: outputMessage,
    stdout: `\x1b[36m${outputMessage}\x1b[0m`,
    sandbox: progressSandbox ?? buildRunningSandboxSummary(displayKey, params, operation)
  }

  if (commandLine) {
    result.command = {
      line: commandLine,
      cmd: readRecordString(params, 'cmd') ?? readRecordString(params, 'command'),
      args: readRecordStringArray(params, 'args')
    }
  }

  if (operation === 'code') {
    result.operation = 'code'
  }

  if (operation === 'preview') {
    const port = readRecordNumber(params, 'port') ?? 3000
    const previewUrl =
      readRecordString(params, 'previewUrl') ??
      (progressData ? readRecordString(progressData, 'previewUrl') : null)
    result.port = port
    if (previewUrl) {
      result.previewUrl = previewUrl
    }
  }

  if (operation === 'files') {
    const files = Array.isArray(params.files) ? params.files : []
    result.files = files
      .map((file, index) => {
        const record = asRecord(file)
        const path = record ? readRecordString(record, 'path') : null
        const content = record && typeof record.content === 'string' ? record.content : ''

        return {
          path: path ?? `file-${index + 1}`,
          bytes: content.length
        }
      })
      .slice(0, 20)
  }

  if (operation === 'browser') {
    result.browser = buildRunningBrowserRecord(params, progressData)
  }

  return result
}

// --------------

function buildRunningDesktopResult(toolExecution: ToolExecutionInfo, displayKey: string) {
  const params = getRequestParams(toolExecution.request)
  const action = displayKey.split('__')[1] ?? readRecordString(params, 'action') ?? 'start_session'

  return {
    success: true,
    status: 'running',
    action,
    message: toolExecution.message ?? 'Computer control is running...',
    session: {
      status: 'running',
      id: readRecordString(params, 'sessionId') ?? readRecordString(params, 'session_id')
    }
  }
}

export const LoadingMessage: React.FC<LoadingMessageProps> = ({
  isGenerating,
  currentSection,
  showToolsOnly = false,
  chatId = null,
  generationStartedAt = null,
  onPoseChange
}) => {
  const { t } = useTranslation()
  const { isImageGenerating, setIsImageGenerating } = useContext(ChatbotUIChatContext)
  const { addEventListener, removeEventListener, lastEvent } = useServerEvents()

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)
  const [showWaitingMessage, setShowWaitingMessage] = useState(false)
  const waitingTimerRef = useRef<NodeJS.Timeout | null>(null)
  const rotationTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [dots, setDots] = useState('')
  const [message, setMessage] = useState('')
  const [reasoningSummary, setReasoningSummary] = useState<string | null>(null)
  const [isReasoning, setIsReasoning] = useState(false)
  const [reasoningText, setReasoningText] = useState('')
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  // Track tool execution status
  const [toolExecution, setToolExecution] = useState<ToolExecutionInfo | null>(null)
  // Track current step in process
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const stepIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const toolResetTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isGeneratingRef = useRef(isGenerating)
  const lastToolStartEventKeyRef = useRef<string | null>(null)
  const mountedAtRef = useRef(Date.now())

  // Add animated progress state and animation frame reference
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const animationRef = useRef<number | null>(null)
  const targetProgressRef = useRef(0)

  const clearToolResetTimer = useCallback(() => {
    if (toolResetTimerRef.current) {
      clearTimeout(toolResetTimerRef.current)
      toolResetTimerRef.current = null
    }
  }, [])

  const shouldProcessToolEvent = useCallback(
    (eventChatId: string | null | undefined) => {
      if (!chatId || chatId.length === 0) return true
      if (!eventChatId || eventChatId.length === 0) return true
      return eventChatId === chatId
    },
    [chatId]
  )

  const shouldProcessEventTimestamp = useCallback(
    (timestamp: number | undefined) => {
      if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) return true

      const eventLowerBound = generationStartedAt ?? mountedAtRef.current
      return timestamp >= eventLowerBound - 500
    },
    [generationStartedAt]
  )

  const getToolStartEventKey = useCallback((event: ToolStartEvent) => {
    if (typeof event.id === 'string' && event.id.length > 0) {
      return event.id
    }

    const toolId = event.data?.toolId
    if (typeof toolId !== 'string' || toolId.length === 0) {
      return null
    }

    const createdAt = typeof event.createdAt === 'string' ? event.createdAt : ''
    return `${toolId}:${createdAt}`
  }, [])

  const resolveToolIdentifiers = useCallback(
    (
      data:
        | {
            toolId?: string
            toolName?: string
          }
        | null
        | undefined
    ) => {
      const toolId = typeof data?.toolId === 'string' && data.toolId.length > 0 ? data.toolId : null
      const toolName =
        typeof data?.toolName === 'string' && data.toolName.length > 0 ? data.toolName : null
      const resolvedToolId = toolId ?? toolName
      if (!resolvedToolId) return null

      return {
        resolvedToolId,
        displayId: toolName ?? resolvedToolId
      }
    },
    []
  )

  const extractDisplayTitleFromRequest = useCallback((request: unknown) => {
    const pickString = (value: unknown): string | null => {
      if (typeof value !== 'string') return null
      const trimmed = value.trim()
      return trimmed.length > 0 ? trimmed : null
    }

    if (!request || typeof request !== 'object') {
      return null
    }

    const direct = request as Record<string, unknown>
    const keys = [
      'query',
      'q',
      'objective',
      'prompt',
      'input',
      'url',
      'location',
      'searchTerm',
      'term',
      'text',
      'topic',
      'question',
      'title',
      'name',
      'email',
      'conversationTitle',
      'spaceName'
    ] as const

    for (const key of keys) {
      const value = pickString(direct[key])
      if (value) return value
    }

    const nestedRaw = direct.parameters
    if (!nestedRaw || typeof nestedRaw !== 'object') {
      return null
    }

    const nested = nestedRaw as Record<string, unknown>
    for (const key of keys) {
      const value = pickString(nested[key])
      if (value) return value
    }

    return null
  }, [])

  const isWebSearchTool = useCallback((toolKey: string) => {
    const normalized = toolKey.toLowerCase()
    return normalized.includes('googlesearch') || normalized.includes('webscraper__googlesearch')
  }, [])

  const isSpacesTool = useCallback((toolKey: string) => {
    return toolKey.startsWith('losiSpaces__')
  }, [])

  const isMcpOrSkillTool = useCallback((toolKey: string) => {
    return toolKey.startsWith('MCPIntegrationTool__') || toolKey.startsWith('SkillIntegrationTool__')
  }, [])

  const buildToolActionArgs = useCallback((data: ToolStartEventData) => {
    const displayTitle = typeof data.displayTitle === 'string' ? data.displayTitle.trim() : ''
    const requestRecord =
      data.request && typeof data.request === 'object' && !Array.isArray(data.request)
        ? (data.request as Record<string, unknown>)
        : null

    return {
      ...(requestRecord ?? {}),
      ...(displayTitle.length > 0 ? { displayTitle } : {})
    }
  }, [])

  const resolveWebSearchStartMessage = useCallback(
    (data: ToolStartEventData, displayId: string) => {
      if (!isWebSearchTool(displayId)) return null

      const explicitTitle = typeof data.displayTitle === 'string' ? data.displayTitle.trim() : ''
      const query =
        explicitTitle.length > 0 ? explicitTitle : extractDisplayTitleFromRequest(data.request)

      return query ? `Searching for "${query}"` : null
    },
    [extractDisplayTitleFromRequest, isWebSearchTool]
  )

  const resolveSpacesToolStartMessage = useCallback(
    (data: ToolStartEventData, displayId: string) => {
      if (!isSpacesTool(displayId)) return null

      return getPlatformToolActionLabel(displayId, buildToolActionArgs(data))
    },
    [buildToolActionArgs, isSpacesTool]
  )

  const resolveIntegrationToolStartMessage = useCallback(
    (data: ToolStartEventData, displayId: string) => {
      if (!isMcpOrSkillTool(displayId)) return null

      return getPlatformToolActionLabel(displayId, buildToolActionArgs(data))
    },
    [buildToolActionArgs, isMcpOrSkillTool]
  )

  const isGenericToolProgressMessage = useCallback((value: string) => {
    const normalized = value.trim().toLowerCase()
    return (
      normalized === 'running…' ||
      normalized === 'running...' ||
      normalized === 'starting tool…' ||
      normalized === 'starting tool...' ||
      normalized === 'running'
    )
  }, [])

  // Get and shuffle waiting messages once on component mount
  const [waitingMessages] = useState(() => {
    const messages = t('auth.loading.waiting_messages', { returnObjects: true }) as string[]
    return shuffleArray(messages || [])
  })

  // Resolve display key: prefer tool name (e.g. webScraper__googleSearch) over toolId (e.g. platform-webScraper__googleSearch-123)
  const getToolDisplayKey = (exec: { toolId: string; toolName: string } | null) =>
    exec ? exec.toolName || exec.toolId : ''

  // Get the tool display info based on tool ID or tool name
  const getToolDisplayInfo = (toolId: string) => {
    // Extract the base tool name from the tool ID (remove any function part after __)
    const parts = toolId.split('__')
    const baseTool = parts[0]
    const fullToolId = toolId

    // First check if the exact tool ID has a match
    if (fullToolId in TOOL_DISPLAY_INFO) {
      return TOOL_DISPLAY_INFO[fullToolId]
    }

    // Then check if the base tool has a match
    if (baseTool in TOOL_DISPLAY_INFO) {
      return TOOL_DISPLAY_INFO[baseTool]
    }

    // Otherwise, look for a partial match
    for (const [key, info] of Object.entries(TOOL_DISPLAY_INFO)) {
      if (baseTool.toLowerCase().includes(key.toLowerCase())) {
        return info
      }
    }

    if (fullToolId.startsWith('losiSpaces__')) {
      const functionId = fullToolId.slice('losiSpaces__'.length)

      return {
        ...DEFAULT_TOOL_INFO,
        name: LOSI_SPACES_RUNNING_TOOL_LABELS[functionId] ?? getToolDisplayLabel(toolId),
        icon: getLosiSpacesToolIcon(functionId),
        description: 'Applying the change in your Space',
        steps: [
          'Reviewing the current Space state',
          'Applying the requested change',
          'Refreshing the Space view'
        ]
      }
    }

    if (fullToolId.startsWith('sandbox__') || fullToolId === 'sandbox') {
      return {
        ...DEFAULT_TOOL_INFO,
        name: getToolDisplayLabel(toolId),
        icon: <IconDeviceLaptop className="text-muted-foreground size-4" />,
        description: 'Working in Losi Computer',
        steps: [
          'Preparing the computer',
          'Running the requested action',
          'Updating the workspace'
        ]
      }
    }

    if (fullToolId.startsWith('ContentEngine__')) {
      return {
        ...DEFAULT_TOOL_INFO,
        name: getToolDisplayLabel(toolId),
        icon: <IconCpu className="size-4 text-muted-foreground" />,
        description: 'Running Content Engine',
        steps: [
          'Loading Engine context',
          'Preparing the approval stage',
          'Saving durable Engine output',
          'Updating Chat with the result'
        ]
      }
    }

    if (fullToolId.startsWith('LosiProductCreation__')) {
      return {
        ...DEFAULT_TOOL_INFO,
        name: getToolDisplayLabel(toolId),
        icon: <IconSparkle2 className="size-4 text-muted-foreground" />,
        description: 'Creating a Losi product from chat',
        steps: [
          'Reading the Super Agent brief',
          'Creating the durable workspace artifact',
          'Preparing the review surface',
          'Updating Chat with the result'
        ]
      }
    }

    // If no match found, return default with normalized name from tool-labels
    return { ...DEFAULT_TOOL_INFO, name: getToolDisplayLabel(toolId) }
  }

  // Cycle through the steps for the current tool
  useEffect(() => {
    if (toolExecution) {
      // Clear any existing interval
      if (stepIntervalRef.current) {
        clearInterval(stepIntervalRef.current)
      }

      const displayKey = getToolDisplayKey(toolExecution)
      const toolInfo = getToolDisplayInfo(displayKey)
      const steps = toolInfo.steps || DEFAULT_TOOL_INFO.steps

      // Start cycling through steps
      setCurrentStepIndex(0)

      // Update the step every few seconds
      stepIntervalRef.current = setInterval(() => {
        setCurrentStepIndex((prev) => {
          const nextIndex = prev + 1
          // If we've gone through all steps, start over
          return nextIndex >= steps.length ? 0 : nextIndex
        })
      }, 3000)
    } else {
      // Clear interval when not executing a tool
      if (stepIntervalRef.current) {
        clearInterval(stepIntervalRef.current)
        stepIntervalRef.current = null
      }
    }

    return () => {
      if (stepIntervalRef.current) {
        clearInterval(stepIntervalRef.current)
        stepIntervalRef.current = null
      }
    }
  }, [toolExecution])

  // Smooth progress animation
  useEffect(() => {
    if (toolExecution && toolExecution.progress !== undefined) {
      // Update the target progress
      targetProgressRef.current = Math.max(5, Math.min(100, toolExecution.progress))

      // Start the animation if not running
      if (animationRef.current === null) {
        const animateProgress = () => {
          setAnimatedProgress((prev) => {
            // Smoothly move toward target value
            const diff = targetProgressRef.current - prev
            const step = Math.max(0.5, Math.abs(diff) * 0.1)

            // Calculate new progress
            const newProgress = prev + (diff > 0 ? step : -step)

            // Stop animation when very close to target
            if (Math.abs(newProgress - targetProgressRef.current) < 0.5) {
              if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
                animationRef.current = null
              }
              return targetProgressRef.current
            }

            // Continue animation
            animationRef.current = requestAnimationFrame(animateProgress)
            return newProgress
          })
        }

        animationRef.current = requestAnimationFrame(animateProgress)
      }
    } else {
      // Reset animation when tool execution is done
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      setAnimatedProgress(0)
    }

    // Clean up animation frame on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
  }, [toolExecution])

  // Handle tool start event
  const handleToolStart = useCallback(
    (event: ToolStartEvent) => {
      const eventKey = getToolStartEventKey(event)
      if (eventKey && lastToolStartEventKeyRef.current === eventKey) return

      const data = event.data
      const identifiers = resolveToolIdentifiers(data)

      if (!data || !identifiers) {
        return
      }

      if (!shouldProcessToolEvent(data.chatId)) return
      if (!shouldProcessEventTimestamp(event.timestamp)) return
      if (eventKey) lastToolStartEventKeyRef.current = eventKey

      // A tool can start before React flushes `isGenerating=true` for this
      // message. Treat tool.start itself as authoritative run-state.
      isGeneratingRef.current = true
      clearToolResetTimer()

      const { resolvedToolId, displayId } = identifiers
      const displayInfo = getToolDisplayInfo(displayId)
      const toolStartMessage =
        resolveWebSearchStartMessage(data, displayId) ||
        resolveSpacesToolStartMessage(data, displayId) ||
        resolveIntegrationToolStartMessage(data, displayId)

      setToolExecution({
        toolId: resolvedToolId,
        toolName: displayId,
        status: 'running',
        progress: 0,
        step: displayInfo.steps[0],
        request: data.request,
        progressData: asRecord(data) ?? undefined,
        ...(toolStartMessage ? { message: toolStartMessage } : {})
      })

      setMessage(toolStartMessage ?? displayInfo.description)
      setShowWaitingMessage(false)
      setCurrentStepIndex(0)
    },
    [
      clearToolResetTimer,
      getToolStartEventKey,
      resolveToolIdentifiers,
      resolveIntegrationToolStartMessage,
      resolveSpacesToolStartMessage,
      resolveWebSearchStartMessage,
      shouldProcessEventTimestamp,
      shouldProcessToolEvent
    ]
  )

  // Handle tool progress event
  const handleToolProgress = useCallback(
    (event: any) => {
      if (!isGeneratingRef.current) return
      if (!shouldProcessToolEvent(event?.data?.chatId)) return
      if (!shouldProcessEventTimestamp(event?.timestamp)) return

      const data = event?.data
      const identifiers = resolveToolIdentifiers(data)
      if (!identifiers) return

      const { resolvedToolId, displayId } = identifiers
      const progressVal =
        typeof data.progress === 'object' &&
        data.progress != null &&
        typeof (data.progress as { percent?: number }).percent === 'number'
          ? (data.progress as { percent?: number }).percent
          : data.progress
      const rawMessageVal =
        typeof data.progress === 'object' &&
        data.progress != null &&
        typeof (data.progress as { message?: string }).message === 'string'
          ? (data.progress as { message?: string }).message
          : data.message
      const messageVal = typeof rawMessageVal === 'string' ? rawMessageVal.trim() : ''
      const contextualMessage =
        resolveWebSearchStartMessage(data, displayId) ||
        resolveSpacesToolStartMessage(data, displayId) ||
        resolveIntegrationToolStartMessage(data, displayId)
      const dataRecord = asRecord(data) ?? undefined
      const progressRecord = asRecord(dataRecord?.progress) ?? undefined

      setToolExecution((prev) => {
        const isSandboxProgressForActiveComputer =
          resolvedToolId === 'sandbox' && Boolean(prev?.toolName.startsWith('sandbox__'))
        const nextDisplayId = isSandboxProgressForActiveComputer
          ? prev?.toolName ?? displayId
          : displayId
        const nextResolvedToolId = isSandboxProgressForActiveComputer
          ? prev?.toolId ?? resolvedToolId
          : resolvedToolId
        const displayInfo = getToolDisplayInfo(nextDisplayId)
        const previousMessage =
          prev?.toolId === nextResolvedToolId ? prev.message : undefined
        const suppressGenericProgressMessage =
          messageVal.length > 0 &&
          isGenericToolProgressMessage(messageVal) &&
          ((typeof previousMessage === 'string' && previousMessage.trim().length > 0) ||
            (typeof contextualMessage === 'string' && contextualMessage.trim().length > 0))
        const nextMessage =
          messageVal.length > 0 && !suppressGenericProgressMessage
            ? messageVal
            : (previousMessage ?? contextualMessage)
        const progressData = {
          ...(prev?.progressData ?? {}),
          ...(progressRecord ?? {}),
          ...(dataRecord ?? {})
        }
        const request =
          data.request ??
          (prev?.toolId === nextResolvedToolId ? prev.request : undefined)

        if (!prev || prev.toolId !== nextResolvedToolId) {
          return {
            toolId: nextResolvedToolId,
            toolName: nextDisplayId,
            status: 'running',
            progress: typeof progressVal === 'number' ? progressVal : 0,
            step: displayInfo.steps[0],
            request,
            progressData,
            ...(nextMessage ? { message: nextMessage } : {})
          }
        }

        return {
          ...prev,
          status: prev.status === 'complete' || prev.status === 'error' ? prev.status : 'running',
          progress: typeof progressVal === 'number' ? progressVal : prev.progress,
          request,
          progressData,
          message: nextMessage ?? prev.message
        }
      })

      if (messageVal.length > 0 && !isGenericToolProgressMessage(messageVal)) {
        setMessage(messageVal)
      } else {
        const displayInfo = getToolDisplayInfo(displayId)
        setMessage((previous) => previous || contextualMessage || displayInfo.description)
      }
      setShowWaitingMessage(false)
    },
    [
      isGenericToolProgressMessage,
      resolveToolIdentifiers,
      resolveIntegrationToolStartMessage,
      resolveSpacesToolStartMessage,
      resolveWebSearchStartMessage,
      shouldProcessEventTimestamp,
      shouldProcessToolEvent
    ]
  )

  // Handle tool completion event
  const handleToolComplete = useCallback(
    (event: any) => {
      if (!isGeneratingRef.current) return
      if (!shouldProcessToolEvent(event?.data?.chatId)) return
      if (!shouldProcessEventTimestamp(event?.timestamp)) return

      const data = event?.data
      const identifiers = resolveToolIdentifiers(data)
      if (!identifiers) return
      const { resolvedToolId, displayId } = identifiers

      setToolExecution((prev) => {
        if (!prev || prev.toolId !== resolvedToolId) {
          return {
            toolId: resolvedToolId,
            toolName: displayId,
            status: 'complete',
            result: data.result,
            request: data.request,
            progressData: asRecord(data) ?? undefined,
            progress: 100
          }
        }

        return {
          ...prev,
          status: 'complete',
          result: data.result,
          request: data.request ?? prev.request,
          progressData: {
            ...(prev.progressData ?? {}),
            ...(asRecord(data) ?? {})
          },
          progress: 100
        }
      })

      // Update target progress to 100% for completion animation
      targetProgressRef.current = 100

      const displayInfo = getToolDisplayInfo(displayId)
      setMessage(`${displayInfo.name} complete`)

      // Reset the tool execution info after a delay
      clearToolResetTimer()
      toolResetTimerRef.current = setTimeout(() => {
        setToolExecution(null)
        setMessage('')
        toolResetTimerRef.current = null
      }, 2000)
    },
    [
      clearToolResetTimer,
      resolveToolIdentifiers,
      shouldProcessEventTimestamp,
      shouldProcessToolEvent
    ]
  )

  // Handle tool error event
  const handleToolError = useCallback(
    (event: any) => {
      if (!isGeneratingRef.current) return
      if (!shouldProcessToolEvent(event?.data?.chatId)) return
      if (!shouldProcessEventTimestamp(event?.timestamp)) return

      const data = event?.data
      const identifiers = resolveToolIdentifiers(data)
      if (!identifiers) return
      const { resolvedToolId, displayId } = identifiers

      setToolExecution((prev) => {
        if (!prev || prev.toolId !== resolvedToolId) {
          return {
            toolId: resolvedToolId,
            toolName: displayId,
            status: 'error',
            error: data.error,
            request: data.request,
            progressData: asRecord(data) ?? undefined,
            progress: 0
          }
        }

        return {
          ...prev,
          status: 'error',
          error: data.error,
          request: data.request ?? prev.request,
          progressData: {
            ...(prev.progressData ?? {}),
            ...(asRecord(data) ?? {})
          },
          progress: 0
        }
      })

      // Show a more user-friendly error message
      const errorMsg = data.error || 'Something went wrong'
      setMessage(`Error: ${errorMsg.length > 50 ? errorMsg.substring(0, 50) + '...' : errorMsg}`)

      // Reset the tool execution info after a delay
      clearToolResetTimer()
      toolResetTimerRef.current = setTimeout(() => {
        setToolExecution(null)
        setMessage('')
        toolResetTimerRef.current = null
      }, 3000)
    },
    [
      clearToolResetTimer,
      resolveToolIdentifiers,
      shouldProcessEventTimestamp,
      shouldProcessToolEvent
    ]
  )

  // Reasoning/thinking event handlers are stable so we can attach early to
  // avoid race conditions. We filter by chatId + generation-start timestamp
  // because Supabase Realtime can deliver the previous turn's tail
  // (message.thinking.end) shortly after a new pending message mounts —
  // without the filter, the new loader shows the previous turn's reasoning.
  // Prefer event.timestamp (from row created_at / inline dispatch); fall back
  // to data.timestamp (emitServerEvent payload) so neither path is dropped.
  const resolveEventTimestamp = useCallback((event: any) => {
    const eventTs = event?.timestamp
    if (typeof eventTs === 'number' && Number.isFinite(eventTs)) return eventTs
    const dataTs = event?.data?.timestamp
    if (typeof dataTs === 'number' && Number.isFinite(dataTs)) return dataTs
    return undefined
  }, [])

  const onThinkingStart = useCallback(
    (event: any) => {
      const { data } = event
      if (!shouldProcessToolEvent(data?.chatId)) return
      if (!shouldProcessEventTimestamp(resolveEventTimestamp(event))) return
      setIsReasoning(true)
      if (data?.message) setReasoningText(data.message)
    },
    [shouldProcessToolEvent, shouldProcessEventTimestamp, resolveEventTimestamp]
  )
  const onThinkingUpdate = useCallback(
    (event: any) => {
      const { data } = event
      if (!shouldProcessToolEvent(data?.chatId)) return
      if (!shouldProcessEventTimestamp(resolveEventTimestamp(event))) return
      if (data?.message) setReasoningText(data.message)
    },
    [shouldProcessToolEvent, shouldProcessEventTimestamp, resolveEventTimestamp]
  )
  const onThinkingEnd = useCallback(
    (event: any) => {
      const { data } = event
      if (!shouldProcessToolEvent(data?.chatId)) return
      if (!shouldProcessEventTimestamp(resolveEventTimestamp(event))) return
      setIsReasoning(false)
      if (data?.summary) setReasoningSummary(String(data.summary))
    },
    [shouldProcessToolEvent, shouldProcessEventTimestamp, resolveEventTimestamp]
  )

  // Always register thinking events to avoid timing issues with isGenerating
  useEffect(() => {
    addEventListener('message.thinking.start', onThinkingStart)
    addEventListener('message.thinking.update', onThinkingUpdate)
    addEventListener('message.thinking.end', onThinkingEnd)
    return () => {
      removeEventListener('message.thinking.start', onThinkingStart)
      removeEventListener('message.thinking.update', onThinkingUpdate)
      removeEventListener('message.thinking.end', onThinkingEnd)
    }
  }, [addEventListener, removeEventListener, onThinkingStart, onThinkingUpdate, onThinkingEnd])

  // Keep tool listeners mounted so early tool.start events are not missed.
  useEffect(() => {
    addEventListener('tool.start', handleToolStart)
    addEventListener('tool.progress', handleToolProgress)
    addEventListener('tool.complete', handleToolComplete)
    addEventListener('tool.error', handleToolError)
    return () => {
      removeEventListener('tool.start', handleToolStart)
      removeEventListener('tool.progress', handleToolProgress)
      removeEventListener('tool.complete', handleToolComplete)
      removeEventListener('tool.error', handleToolError)
    }
  }, [
    addEventListener,
    removeEventListener,
    handleToolStart,
    handleToolProgress,
    handleToolComplete,
    handleToolError
  ])

  // Only wipe tool/reasoning UI when the generation identity actually changes.
  // Remounts (or clearToolResetTimer identity churn) must not clear live tool
  // state mid-turn — that is how tools that fire during reasoning vanish.
  const prevGenerationStartedAtRef = useRef<number | null | undefined>(
    undefined
  )
  useEffect(() => {
    const prev = prevGenerationStartedAtRef.current
    prevGenerationStartedAtRef.current = generationStartedAt
    if (prev === undefined) return
    if (prev === generationStartedAt) return

    lastToolStartEventKeyRef.current = null
    setToolExecution(null)
    setMessage('')
    setReasoningSummary(null)
    setIsReasoning(false)
    setReasoningText('')
    clearToolResetTimer()
  }, [clearToolResetTimer, generationStartedAt])

  useEffect(() => {
    if (!lastEvent) return
    if (lastEvent.type === 'tool.start') {
      handleToolStart(lastEvent as ToolStartEvent)
      return
    }

    if (lastEvent.type === 'tool.progress') {
      handleToolProgress(lastEvent)
      return
    }

    if (lastEvent.type === 'tool.complete') {
      handleToolComplete(lastEvent)
      return
    }

    if (lastEvent.type === 'tool.error') {
      handleToolError(lastEvent)
    }
  }, [handleToolComplete, handleToolError, handleToolProgress, handleToolStart, lastEvent])

  useEffect(() => {
    isGeneratingRef.current = isGenerating

    if (isGenerating) {
      intervalRef.current = setInterval(() => {
        setDots((prev) => (prev.length < 3 ? prev + '.' : ''))
      }, 500)
    } else {
      clearToolResetTimer()
      if (intervalRef.current) clearInterval(intervalRef.current)
      setDots('')
      setMessage('')
      setToolExecution(null)
      setReasoningSummary(null)
      setIsReasoning(false)
      setReasoningText('')
    }

    return () => {
      clearToolResetTimer()
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [clearToolResetTimer, isGenerating, currentSection])

  useEffect(() => {
    // Rotating "waiting" joke messages were removed; keep the loader showing a
    // stable label instead. No timers to run.
    setShowWaitingMessage(false)
    setCurrentMessageIndex(0)
  }, [isGenerating])

  // Losi avatar pose for the loader. These hooks MUST stay above every early
  // return below (Rules of Hooks): previously they lived after `return null`,
  // so the rendered hook count changed when isGenerating flipped, corrupting
  // React's hook dispatcher and manifesting as a max-update-depth (#185) loop.
  // The pose object is recreated on every render (resolveChatLosiPose returns a
  // fresh literal), so the effect is guarded by a value SIGNATURE to avoid an
  // onPoseChange -> parent setState -> re-render -> onPoseChange feedback loop.
  const poseToolDisplayKey = toolExecution ? getToolDisplayKey(toolExecution) : null
  const losiPose = useMemo(
    () =>
      resolveChatLosiPose({
        mode: 'generating',
        isReasoning,
        toolKey: poseToolDisplayKey,
        toolId: toolExecution?.toolId ?? null,
        isGenerating: isGenerating || Boolean(toolExecution)
      }),
    [isReasoning, poseToolDisplayKey, toolExecution?.toolId, isGenerating]
  )
  const poseLive = isGenerating || isReasoning || Boolean(toolExecution)
  const lastPoseSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    if (!onPoseChange) return
    const nextPose = poseLive ? losiPose : null
    const signature = nextPose
      ? `${nextPose.state}|${nextPose.status}|${nextPose.drive}`
      : 'null'
    if (lastPoseSignatureRef.current === signature) return
    lastPoseSignatureRef.current = signature
    onPoseChange(nextPose)
  }, [onPoseChange, poseLive, losiPose])

  useEffect(() => {
    return () => onPoseChange?.(null)
  }, [onPoseChange])

  if (!isGenerating) return null

  // In tools-only mode (post-first-token), keep showing the spinner + rotating
  // "Loading…" message when no tool is currently executing. Previously this
  // returned null between tool rounds, producing a visible blank gap where
  // users lost confidence the response was still generating.

  if (isImageGenerating) {
    return (
      <div className="flex animate-blur items-center space-x-2">
        <ImageLoading />
      </div>
    )
  }

  // Render tool-specific skeleton based on tool type
  const renderToolSkeleton = () => {
    if (!toolExecution) return null

    const displayKey = getToolDisplayKey(toolExecution) || toolExecution.toolId
    const toolType = displayKey.split('__')[0].toLowerCase()

    if (isComputerLoadingTool(displayKey)) {
      const entry: ToolActivityEntry = {
        key: displayKey,
        name: getToolDisplayLabel(displayKey),
        durationMs: 0,
        request: toolExecution.request,
        result: buildRunningComputerResult(toolExecution, displayKey),
        status: toolExecution.status === 'error' ? 'error' : 'running'
      }

      return (
        <div className="my-3 w-full">
          {renderToolResultForEntry(entry)}
        </div>
      )
    }

    if (isDesktopToolAnnotationKey(displayKey)) {
      const annotation = {
        [displayKey]: {
          result: buildRunningDesktopResult(toolExecution, displayKey),
          status: toolExecution.status === 'error' ? 'error' : 'running',
          requestTime: 0
        }
      } as Annotation2

      return (
        <div className="my-3 w-full">
          <DesktopToolAnnotation annotation={annotation} toolKey={displayKey} />
        </div>
      )
    }

    // Image generation skeleton — same footprint as the clean MediaGenerationCard.
    if (
      toolType.includes('image') ||
      toolType.includes('flux') ||
      toolType.includes('diffusion') ||
      displayKey.includes('unifiedImage')
    ) {
      return (
        <div className="my-2 w-full max-w-xl">
          <div className="bg-secondary/40 aspect-[4/3] w-full overflow-hidden rounded-xl motion-safe:animate-pulse motion-reduce:animate-none">
            <div className="flex h-full items-center justify-center">
              <IconPhoto className="text-muted-foreground/40 size-10" />
            </div>
          </div>
        </div>
      )
    }

    // Video generation tool skeletons - 16:9 aspect ratio
    if (toolType.includes('video')) {
      return (
        <div className="my-4 w-full">
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-secondary/40 motion-safe:animate-pulse motion-reduce:animate-none">
            <div className="flex h-full items-center justify-center">
              <IconVideo className="size-12 text-muted-foreground/50" />
            </div>
          </div>
        </div>
      )
    }

    // Manim animation tool skeleton - 1:1 aspect ratio with math symbols
    if (
      toolType.includes('manim') ||
      toolExecution.toolId === 'manimAnimation' ||
      toolExecution.toolId === 'webScraper__renderManimAnimation'
    ) {
      // Extract the scene name from the progress message if available
      let sceneName = undefined
      if (toolExecution.message) {
        const sceneNameMatch = toolExecution.message.match(/scene "([^"]+)"/)
        if (sceneNameMatch && sceneNameMatch[1]) {
          sceneName = sceneNameMatch[1]
        }
      }

      return (
        <ManimAnimationSkeleton
          progress={toolExecution.progress}
          sceneName={sceneName}
          message={toolExecution.message}
        />
      )
    }

    // Web scraper tool skeleton - match WebSearch component style
    const isWebTool =
      toolType.includes('web') ||
      toolType.includes('search') ||
      toolType.includes('google') ||
      toolType.includes('fetchdatafromurl') ||
      toolType.includes('youtubecaptions')

    if (
      isWebTool &&
      !toolType.includes('weather') &&
      !(toolType.includes('manim') || displayKey === 'manimAnimation')
    ) {
      return (
        <div className="w-full space-y-4">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div
                key={`web-result-${i}`}
                className="h-16 overflow-hidden rounded-md bg-secondary/40 p-2 motion-safe:animate-pulse motion-reduce:animate-none"
              >
                <div className="mb-1 flex items-center space-x-1">
                  <div className="size-4 rounded-full bg-secondary/60"></div>
                  <div className="h-3 w-16 rounded bg-secondary/60"></div>
                </div>
                <div className="h-2.5 w-full rounded bg-secondary/60"></div>
                <div className="mt-1 h-2.5 w-5/6 rounded bg-secondary/60"></div>
              </div>
            ))}
          </div>

          {toolType.includes('search') && (
            <div className="mt-3 grid grid-cols-1 items-center justify-center gap-4 md:grid-cols-2">
              <div className="aspect-video w-full rounded-lg bg-secondary/40 motion-safe:animate-pulse motion-reduce:animate-none">
                <div className="flex h-full items-center justify-center">
                  <IconBrandYoutube className="size-8 text-muted-foreground/50" />
                </div>
              </div>
            </div>
          )}
        </div>
      )
    }

    // Weather tool skeleton
    if (toolType.includes('weather')) {
      return (
        <div className="my-2 w-full overflow-hidden rounded-lg bg-secondary/30 p-4">
          <div className="flex justify-between">
            <div className="space-y-2">
              <div className="h-5 w-32 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
              <div className="h-10 w-20 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
            </div>
            <div className="size-16 rounded-full bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          </div>
          <div className="mt-4 flex justify-between">
            {[...Array(5)].map((_, i) => (
              <div key={`hour-${i}`} className="flex flex-col items-center space-y-2">
                <div className="h-3 w-10 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                <div className="size-8 rounded-full bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                <div className="h-3 w-6 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    if (displayKey.startsWith('losiSpaces__')) {
      return (
        <div className="my-3 w-full rounded-lg bg-secondary/30 p-3">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="h-5 w-40 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none" />
            <div className="h-5 w-20 rounded-full bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none" />
          </div>
          <div className="space-y-2">
            {[...Array(3)].map((_, index) => (
              <div key={`spaces-loader-${index}`} className="flex items-start gap-3">
                <div className="mt-1 size-4 rounded-md bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none" />
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-full rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none" />
                  <div className="h-3 w-2/3 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // Yahoo Finance tool skeleton
    if (toolType.includes('yahoo') || toolType.includes('finance')) {
      return (
        <div className="my-3 w-full overflow-hidden rounded-lg bg-secondary/30 p-3">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="size-8 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
              <div className="h-6 w-32 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
            </div>
            <div className="h-8 w-20 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          </div>
          <div className="h-40 w-full rounded bg-secondary/40 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <div className="h-6 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
            <div className="h-6 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
            <div className="h-6 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          </div>
        </div>
      )
    }

    // Task automation skeleton
    if (toolType.includes('task') || toolType.includes('automation')) {
      return (
        <div className="my-2 w-full space-y-2 rounded-lg bg-secondary/30 p-3">
          <div className="h-5 w-3/4 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={`task-${i}`} className="flex items-start space-x-2">
                <div className="mt-0.5 size-4 rounded-full bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                <div className="space-y-1">
                  <div className="h-4 w-60 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                  <div className="h-3 w-40 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // CRM tool skeletons
    if (
      toolType.includes('create_lead') ||
      toolType.includes('update_lead') ||
      toolType.includes('bulk_update_leads') ||
      toolType.includes('search_leads') ||
      toolType.includes('get_lead_details') ||
      toolType.includes('get_email_engagement') ||
      toolType.includes('get_lead_insights') ||
      toolType.includes('suggest_follow_up') ||
      toolType.includes('send_email') ||
      toolType.includes('send_sms') ||
      toolType.includes('schedule_activity') ||
      toolType.includes('log_activity')
    ) {
      return (
        <div className="my-2 w-full space-y-2 rounded-lg bg-secondary/30 p-3">
          <div className="h-5 w-2/3 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => (
              <div key={`crm-${i}`} className="flex items-center space-x-3">
                <div className="size-8 rounded-full bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-48 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                  <div className="h-3 w-32 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // Agent management tool skeletons
    if (
      toolType.includes('list_agents') ||
      toolType.includes('get_agent') ||
      toolType.includes('create_agent') ||
      toolType.includes('update_agent') ||
      toolType.includes('improve_agent')
    ) {
      return (
        <div className="my-2 w-full space-y-2 rounded-lg bg-secondary/30 p-3">
          <div className="h-5 w-2/3 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
          <div className="space-y-2">
            {[...Array(2)].map((_, i) => (
              <div key={`agent-${i}`} className="flex items-center space-x-3">
                <div className="size-10 rounded-lg bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-4 w-40 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                  <div className="h-3 w-24 rounded bg-secondary/60 motion-safe:animate-pulse motion-reduce:animate-none"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    // Default skeleton for other tool types
    return null
  }

  // Get current tool step description
  const getCurrentStepDescription = () => {
    if (!toolExecution) return null
    const fullToolId = getToolDisplayKey(toolExecution) || toolExecution.toolId
    // For web search and Spaces, always show the dynamic server-side message
    if (fullToolId.includes('googleSearch') || isSpacesTool(fullToolId)) {
      return toolExecution.message || null
    }

    // Otherwise, use static step mapping
    const toolInfo = getToolDisplayInfo(fullToolId)
    const steps = toolInfo.steps || DEFAULT_TOOL_INFO.steps

    if (currentStepIndex < steps.length) {
      return steps[currentStepIndex]
    }

    return toolInfo.description
  }

  // When post-first-token (showToolsOnly=true) and no tool is currently
  // executing, we STILL show the spinner + waiting message so the user knows
  // generation is ongoing between tool rounds.
  // Tools can (and do) run interleaved with reasoning — never hide the live
  // tool row behind isReasoning or the turn looks like a stuck "Thought for Xs".
  const showSpinnerAndWaiting = !toolExecution && !isReasoning
  const toolDisplayKey = toolExecution ? getToolDisplayKey(toolExecution) : null

  return (
    <div className="flex flex-col space-y-2">
      <div className="flex items-center space-x-2">
        {toolExecution ? (
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={getToolDisplayKey(toolExecution)}
              initial={{ opacity: 0, y: 6, filter: 'blur(3px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -6, filter: 'blur(2px)' }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col"
            >
              <div className="flex items-center space-x-2 text-muted-foreground">
                {getToolDisplayInfo(getToolDisplayKey(toolExecution)).icon}
                <span className="text-sm font-medium">
                  {getToolDisplayInfo(getToolDisplayKey(toolExecution)).name}
                  <span className="ml-1 motion-safe:animate-pulse motion-reduce:animate-none">
                    {dots || '...'}
                  </span>
                </span>
              </div>

              {/* Show current step as subtitle */}
              <span className="text-xs italic">
                <ShinyText
                  text={getCurrentStepDescription() || ''}
                  className="text-sm font-medium"
                />
              </span>
            </motion.div>
          </AnimatePresence>
        ) : !isReasoning ? (
          <span className="text-sm">{message}</span>
        ) : null}
      </div>

      {/* Live reasoning dropdown — shown during thinking (alongside tools).
          When showToolsOnly, Thought is already rendered from stream
          annotations (ReasoningSummaryBlock) — avoid a second copy here. */}
      {isReasoning && !showToolsOnly && (
        <Reasoning
          className="w-full"
          isStreaming={true}
          defaultOpen={true}
          autoCloseOnEnd={false}
        >
          <ReasoningTrigger />
          <ReasoningContent animateStreaming={false}>
            {reasoningText}
          </ReasoningContent>
        </Reasoning>
      )}

      {/* Tool execution progress bar */}

      {toolExecution && toolExecution.progress !== undefined && (
        <div className="max-w-s w-full">
          <div className="h-1.5 w-full rounded-full bg-secondary/30">
            <div
              className="h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-in-out motion-reduce:transition-none"
              style={{ width: `${animatedProgress}%` }}
            />
          </div>
          {/* {toolExecution.message && (
            <p className="mt-1 text-xs text-foreground/60">{toolExecution.message}</p>
            )} */}
        </div>
      )}

      {/* Tool-specific skeleton UI */}
      {renderToolSkeleton()}

      {/* Reasoning summary after thinking ends (collapsed dropdown) */}
      {!showToolsOnly && reasoningSummary && !isReasoning && (
        <Reasoning
          className="w-full"
          isStreaming={false}
          defaultOpen={false}
          autoCloseOnEnd={true}
        >
          <ReasoningTrigger />
          <ReasoningContent animateStreaming={false}>
            {reasoningSummary}
          </ReasoningContent>
        </Reasoning>
      )}
    </div>
  )
}
